import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import api from "../api/axios";
import { NewsCard } from "./JobNewsSection";

const LABELS = { hiring: "Hiring", layoffs: "Layoffs", campus: "Campus Hiring", government: "Govt Jobs" };
const GRADIENT = { backgroundImage: "linear-gradient(90deg,#7C3AED,#DB2777)" };

export default function JobNewsDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.getItem("token");

  const [news, setNews] = useState(null);
  const [related, setRelated] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | notfound

  const [user] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });

  // guests are sent to login
  useEffect(() => {
    if (!isLoggedIn) navigate("/login", { replace: true, state: { from: `/news/${id}` } });
  }, [id, isLoggedIn, navigate]);

  // load the article
  useEffect(() => {
    if (!isLoggedIn) return;
    setStatus("loading");
    window.scrollTo(0, 0);
    api
      .get(`/news/${id}`)
      .then((r) => {
        setNews(r.data.data);
        setRelated(r.data.related ?? []);
        setStatus("ready");
      })
      .catch(() => setStatus("notfound"));
  }, [id, isLoggedIn]);

  const date = news
    ? new Date(news.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
    : "";

  if (!isLoggedIn) return null;

  return (
    <div
      className="min-h-screen text-[#1E0F3D]"
      style={{ background: "linear-gradient(180deg, #FAF3FC 0%, #FDF2F8 100%)" }}
    >
      <Navbar user={user} />

      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-10">
        <Link to="/news" className="inline-flex items-center gap-1 text-sm font-semibold text-[#7C3AED] hover:underline">
          ← Back to news
        </Link>

        {status === "loading" && (
          <div className="mt-6 border border-dashed border-gray-300 rounded-xl py-16 text-center text-gray-400">
            Loading article...
          </div>
        )}

        {status === "notfound" && (
          <div className="mt-6 border border-dashed border-gray-300 rounded-xl py-16 px-6 text-center">
            <p className="text-lg font-bold text-[#1E0F3D] mb-1">Article not found</p>
            <p className="text-gray-400">This article may have been removed.</p>
          </div>
        )}

        {status === "ready" && news && (
          <article className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            {news.image_url && (
              <img
                src={news.image_url}
                alt=""
                className="w-full max-h-96 object-cover"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            )}

            <div className="p-6 sm:p-8">
              <span className="inline-block text-[11px] font-bold px-3 py-1 rounded-full bg-[#F5F0FA] text-[#7C3AED]">
                {LABELS[news.category] || "News"}
              </span>

              <h1 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight leading-tight">{news.title}</h1>

              <p className="mt-3 text-sm text-gray-500">
                {news.source_name} · {date}
              </p>

              <hr className="my-6 border-gray-100" />

              {news.description && (
                <p className="text-lg text-gray-700 leading-relaxed">{news.description}</p>
              )}

              {news.content && news.content !== news.description && (
                <p className="mt-4 text-gray-600 leading-relaxed">{news.content}</p>
              )}

              <div className="mt-8 border border-dashed border-purple-200 bg-[#FBF5FF] rounded-xl p-5 flex flex-wrap items-center justify-between gap-4">
                <p className="text-sm text-gray-600 max-w-md">
                  This is a summary from {news.source_name || "the publisher"}. Read the complete story at the source.
                </p>
                <a
                  href={news.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white px-6 py-3 rounded-xl font-semibold text-sm shadow-[0_10px_30px_-8px_rgba(124,58,237,0.6)] hover:opacity-90 transition"
                  style={GRADIENT}
                >
                  Read full article →
                </a>
              </div>
            </div>
          </article>
        )}

        {status === "ready" && news && related.length > 0 && (
          <section className="mt-12">
            <h2
              className="text-xl font-bold mb-5"
              style={{
                ...GRADIENT,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
                display: "inline-block",
              }}
            >
              More {LABELS[news.category]?.toLowerCase()} news
            </h2>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((n) => <NewsCard key={n.id} n={n} />)}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}